/**
 * LLM-assisted idea crafting
 *
 * Suggests improved title and description for backlog submissions.
 * Registry: idea_crafting — inline assist. Transport: callFeatureLlm.
 */

import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const CRAFTING_SYSTEM_PROMPT =
  'You suggest clearer titles and descriptions for prioritization ideas. Respond with valid JSON only.';

export interface CraftIdeaResult {
  suggestedTitle: string;
  suggestedDescription: string;
  tips?: string[];
  reasoning?: string;
}

export async function craftIdea(draft: {
  title: string;
  description?: string;
}): Promise<CraftIdeaResult> {
  if (!isAiCapabilityEnabled('idea_crafting')) {
    throw new Error('AI improvement is disabled for this workspace.');
  }

  const hasContent = draft.title.trim() || (draft.description ?? '').trim();
  if (!hasContent) {
    throw new Error('Enter a title or description before asking AI to improve it.');
  }

  const content = await callFeatureLlm({
    userPrompt: buildCraftingPrompt(draft),
    systemPrompt: CRAFTING_SYSTEM_PROMPT,
    conversationId: 'idea-craft-' + Date.now(),
    model: DEFAULT_MODEL,
    temperature: 0.4,
    maxTokens: 500,
  });

  if (!content) {
    throw new Error(
      'No AI provider available. Configure Peak LLM or add an API key in Settings → Connectors.',
    );
  }

  return parseCraftingResponse(content, draft);
}

function buildCraftingPrompt(draft: { title: string; description?: string }): string {
  const desc = (draft.description ?? '').trim();
  return `You are a product manager helping someone write a clear, actionable backlog item. Rewrite the draft below so evaluators can quickly understand what is being asked for and why it matters. Always produce a rewritten version — do not return the input unchanged.

DRAFT:
Title: ${draft.title.trim() || '(none)'}
${desc ? `Description: ${desc}` : 'Description: (not provided)'}

RULES:
- suggestedTitle: one concise line, verb-first if possible (e.g. "Add…", "Automate…", "Reduce…"). Remove filler words.
- suggestedDescription: 2-4 sentences covering: what is wanted, the benefit/outcome, and optionally who it helps. If no description was provided, infer one from the title.
- Do NOT copy the input verbatim. Always rewrite to improve clarity and specificity.
- Use plain language. Avoid jargon.

Return ONLY valid JSON, no markdown fences:
{"suggestedTitle":"…","suggestedDescription":"…","tips":["…"],"reasoning":"…"}`;
}

function parseCraftingResponse(
  content: string,
  fallback: { title: string; description?: string },
): CraftIdeaResult {
  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      return {
        suggestedTitle:
          typeof parsed.suggestedTitle === 'string'
            ? parsed.suggestedTitle.trim()
            : fallback.title.trim(),
        suggestedDescription:
          typeof parsed.suggestedDescription === 'string'
            ? parsed.suggestedDescription.trim()
            : (fallback.description ?? '').trim(),
        tips: Array.isArray(parsed.tips)
          ? parsed.tips.filter((t: unknown) => typeof t === 'string').slice(0, 5)
          : undefined,
        reasoning:
          typeof parsed.reasoning === 'string' ? parsed.reasoning.trim() : undefined,
      };
    }
  } catch (e) {
    console.warn('Parse crafting response failed:', e);
  }

  return {
    suggestedTitle: fallback.title.trim(),
    suggestedDescription: (fallback.description ?? '').trim(),
  };
}
